import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	type UIEventHandler,
} from 'react';
import debounce from 'lodash/debounce';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { Box, xcss } from '@atlaskit/primitives';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsFullPageIssueView } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionUnionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { COLUMN_OPERATION_RESIZE } from '../../common/constants.tsx';
import type { HeaderHeight } from '../../common/types.tsx';
import { useIsInfiniteScrollEnabled } from '../../controllers/features/selectors.tsx';
import { useInfiniteScrollHandler } from '../../controllers/infinite-scroll-handler/index.tsx';
import { ScrollStateProvider } from '../../controllers/scroll-state/index.tsx';
import { useSiblingIssueCreateOnScroll } from '../../controllers/sibling-issue-create-handler/index.tsx';
import { useIntersectionObserver } from '../../controllers/table-items-intersection-observer/index.tsx';
import { LoadingManager } from './loading-manager/index.tsx';
import type { ScrollContainerProps } from './types.tsx';

export const useScrollToTopWhenLoadingNewIssues = ({
	loading,
	resetScroll,
}: {
	loading?: boolean;
	resetScroll: () => void;
}) => {
	const prevLoading = usePrevious(loading);

	// Reseting the scroll when the connection id change will cover any variable change
	useEffect(() => {
		if (prevLoading !== loading && prevLoading === true) {
			resetScroll();
		}
	});
};

export const ScrollContainer = forwardRef<HTMLDivElement, ScrollContainerProps>(
	(
		{
			children,
			footer,
			loading,
			isGrouping,
			isChildIssuesTable = false,
			onScroll: externalOnScroll,
			columnCount,
			...infiniteScrollProps
		},
		ref,
	) => {
		const scrollContainerRef = useRef<HTMLDivElement>(null);
		const siblingIssueCreateOnScroll = useSiblingIssueCreateOnScroll();
		const isInfiniteScrollEnabled = useIsInfiniteScrollEnabled();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const isFullPageIssueView = useIsFullPageIssueView();

		const previousScrollLeftRef = useRef<number>(0);
		const totalScrollableWidthRef = useRef<number>(0);

		const resetScroll = useCallback(() => {
			if (scrollContainerRef.current != null && isInfiniteScrollEnabled) {
				scrollContainerRef.current.scrollTop = 0;
			}
		}, [isInfiniteScrollEnabled]);

		useScrollToTopWhenLoadingNewIssues({ loading, resetScroll });

		const { onLoadingThresholdCheck, onScroll: infiniteScrollOnScroll } =
			useInfiniteScrollHandler(infiniteScrollProps);

		let sendAnalyticsEventDebounced: Function | undefined;

		if (isChildIssuesTable && fg('ken-2879_is_child_issue_table')) {
			const ANALYTICS_EVENT_DEBOUNCE_DELAY = 1000;
			sendAnalyticsEventDebounced = debounce(() => {
				fireTrackAnalytics(createAnalyticsEvent({}), 'childIssuesTable scrolled');
			}, ANALYTICS_EVENT_DEBOUNCE_DELAY);
		}

		// Horizontal scroll analytics function
		const useHorizontalScrollAnalytics = () => {
			const fireDebouncedAnalyticsForHorizontalScroll = useMemo(
				() =>
					debounce((currentScroll: number, totalScrollableWidth: number) => {
						fireUIAnalytics(createAnalyticsEvent({}), 'issueView horizontalScroll', {
							actionSubjectId: 'issueView',
							scrollLeft: currentScroll,
							totalScrollableWidth,
							section: 'childIssueTable',
							columnCount,
						});
					}, 400),
				[],
			);

			return fireDebouncedAnalyticsForHorizontalScroll;
		};

		// Wrapped with feature gate
		const useHorizontalScrollAnalyticsWithFF = functionUnionWithCondition(
			() => fg('issue_view_layout_experiment_feature_gate'),
			useHorizontalScrollAnalytics,
			() => undefined,
		);

		const fireDebouncedAnalyticsForHorizontalScroll = useHorizontalScrollAnalyticsWithFF();

		const onScroll = useCallback<UIEventHandler>(
			(event) => {
				if (
					isChildIssuesTable &&
					typeof sendAnalyticsEventDebounced === 'function' &&
					fg('ken-2879_is_child_issue_table')
				) {
					sendAnalyticsEventDebounced();
				}
				siblingIssueCreateOnScroll?.(event);
				if (isInfiniteScrollEnabled) {
					infiniteScrollOnScroll(event);
				}
				if (fg('jira_list_extended_scroll_container_props')) {
					externalOnScroll?.(event);
				}
				if (
					event.target instanceof HTMLElement &&
					fg('issue_view_layout_experiment_feature_gate')
				) {
					const target = event.target;
					const currentScroll = Math.round(target.scrollLeft);

					if (totalScrollableWidthRef.current === 0) {
						totalScrollableWidthRef.current = Math.round(target.scrollWidth - target.clientWidth);
					}

					// Send horizontal scroll analytics when scrolling right for child issue table
					const isScrollingRight = currentScroll > previousScrollLeftRef.current;
					if (
						isScrollingRight &&
						isChildIssuesTable &&
						isFullPageIssueView &&
						fireDebouncedAnalyticsForHorizontalScroll
					) {
						fireDebouncedAnalyticsForHorizontalScroll(
							currentScroll,
							totalScrollableWidthRef.current,
						);
					}

					previousScrollLeftRef.current = currentScroll;
				}
			},
			[
				siblingIssueCreateOnScroll,
				isInfiniteScrollEnabled,
				infiniteScrollOnScroll,
				sendAnalyticsEventDebounced,
				isChildIssuesTable,
				externalOnScroll,
				fireDebouncedAnalyticsForHorizontalScroll,
				isFullPageIssueView,
			],
		);

		useEffect(() => {
			// Handle auto-scrolling for pragmatic dnd operations like column resizing
			const scrollableElement = scrollContainerRef.current;

			let cleanup: () => void;

			if (scrollableElement) {
				cleanup = autoScrollForElements({
					element: scrollableElement,
					canScroll: ({ source }) => source.data.operation !== COLUMN_OPERATION_RESIZE,
				});
			}

			return () => {
				cleanup?.();
				fireDebouncedAnalyticsForHorizontalScroll?.cancel();
			};
		}, [fireDebouncedAnalyticsForHorizontalScroll]);

		const intersectionObsRef = useIntersectionObserver();
		// eslint-disable-next-line react-hooks/react-compiler
		/* eslint-disable react-hooks/rules-of-hooks */
		const mergedRefs = fg('jira_list_extended_scroll_container_props')
			? useMergeRefs(ref, intersectionObsRef, scrollContainerRef)
			: useMergeRefs(intersectionObsRef, scrollContainerRef);
		/* eslint-enable react-hooks/rules-of-hooks */

		return (
			<ScrollStateProvider scrollRef={scrollContainerRef}>
				<Box xcss={loadingIndicatorContainerStyles}>
					<Box
						xcss={scrollContainerStyles}
						ref={mergedRefs}
						onScroll={onScroll}
						testId="native-issue-table.ui.scroll-container.scroll-container"
					>
						{children}
					</Box>
					{isInfiniteScrollEnabled && (
						<LoadingManager
							isLoadingPrevious={infiniteScrollProps.isLoadingPrevious}
							isLoadingNext={infiniteScrollProps.isLoadingNext}
							onLoadingThresholdCheck={onLoadingThresholdCheck}
							isGrouping={isGrouping}
						/>
					)}
				</Box>
				{footer}
			</ScrollStateProvider>
		);
	},
);

const HEADER_HEIGHT: HeaderHeight = 40;
const scrollContainerStyles = xcss({
	display: 'flex',
	position: 'relative',
	flexFlow: 'column nowrap',
	overflow: 'auto',
	// Since header is sticky, we need to specify a scroll padding that matches its height so that auto-scrolling
	// functionality accounts for this element in its calculations, otherwise header can overlap the selected row.
	scrollPaddingTop: `${HEADER_HEIGHT}px`,
});

const loadingIndicatorContainerStyles = xcss({
	display: 'flex',
	flexFlow: 'column nowrap',
	position: 'relative',
	overflow: 'hidden',
});
